import * as yup from "yup";
import { FormSectionDto, QuestionComponentType } from "@green0meter/api-client";

export const generateValidationSchema = (
  sections?: FormSectionDto[],
): yup.ObjectSchema<Record<string, yup.AnySchema>> => {
  const allQuestions = sections?.flatMap((section) => section.formQuestions || []);

  const schemaFields = allQuestions?.reduce(
    (acc, question) => {
      let fieldSchema: yup.AnySchema;

      if (question.isRequired) {
        switch (question.componentType) {
          case QuestionComponentType.Number:
            fieldSchema = yup
              .number()
              .typeError(`${question.name} must be a number`)
              .required(`${question.name} is required`);
            break;
          case QuestionComponentType.ShortText:
            fieldSchema = yup.string().required(`${question.name} is required`);
            break;
          case QuestionComponentType.Radio:
            fieldSchema = yup
              .string()
              .oneOf(
                question.answerOptions?.map((option) => option.code) || [],
                `Invalid option for ${question.name}`,
              )
              .required(`${question.name} is required`);
            break;
          case QuestionComponentType.Checkbox:
            fieldSchema = yup
              .array()
              .of(yup.string())
              .min(1, `At least one option must be selected for ${question.name}`)
              .required(`${question.name} is required`);
            break;
          case QuestionComponentType.DatePicker:
            fieldSchema = yup
              .date()
              .typeError(`${question.name} must be a valid date`)
              .required(`${question.name} is required`);
            break;
          case QuestionComponentType.Email:
            fieldSchema = yup.string().required(`${question.name} is required`);
            break;
          case QuestionComponentType.Files:
            fieldSchema = yup
              .mixed()
              .test(
                "fileRequired",
                `${question.name} is required`,
                (value) => !!value,
              );
            break;
          case QuestionComponentType.Select:
            fieldSchema = yup
              .string()
              .oneOf(
                question.answerOptions?.map((option) => option.code) || [],
                `Invalid selection for ${question.name}`,
              )
              .required(`${question.name} is required`);
            break;
          case QuestionComponentType.Slider:
            fieldSchema = yup
              .number()
              .min(0, `${question.name} must be at least 0`)
              .max(100, `${question.name} must be at most 100`)
              .required(`${question.name} is required`);
            break;
          case QuestionComponentType.RichTextBox:
            fieldSchema = yup.string().required(`${question.name} is required`);
            break;
          case QuestionComponentType.Table:
            fieldSchema = yup
              .array()
              .of(
                yup.object({
                  id: yup.string().required("ID is required"),
                  value: yup.string().required("Value is required"),
                }),
              )
              .required(`${question.name} is required`);
            break;
          default:
            fieldSchema = yup.mixed().required(`${question.name} is required`);
        }
      } else {
        fieldSchema = yup.mixed().notRequired();
      }

      acc[question.code] = fieldSchema;

      return acc;
    },
    {} as Record<string, yup.AnySchema>,
  );

  return yup.object().shape(schemaFields || {});
};
